import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  DownloadCloud, Loader2, ChevronDown, ChevronUp, FileArchive,
  ShieldCheck, AlertTriangle, CheckCircle2,
} from "lucide-react";
import { api } from "@/lib/api";
import { downloadBlob } from "@/components/riptag/util";

const FORMATS = [
  ["original", "Original"], ["mp3", "MP3"], ["wav", "WAV"], ["flac", "FLAC"],
  ["cd", "Audio CD / Power2Go (WAV + M3U)"],
];

export const usePollJob = (onDone) => {
  const [job, setJob] = useState(null);
  const timer = useRef(null);
  const poll = (jobId) => {
    clearInterval(timer.current);
    timer.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/rip/jobs/${jobId}`);
        setJob(data);
        if (data.status === "done" || data.status === "error") {
          clearInterval(timer.current);
          onDone?.(data);
        }
      } catch { clearInterval(timer.current); }
    }, 1500);
  };
  useEffect(() => () => clearInterval(timer.current), []);
  return [job, setJob, poll];
};

const Progress = ({ job }) => (
  <div className="mt-2">
    <div className="flex justify-between text-[10px] font-mono text-ink-muted mb-1">
      <span>{job.status === "done" ? "Complete" : job.status === "error" ? "Failed" : "Working…"}</span>
      <span>{job.done}/{job.total} files</span>
    </div>
    <div className="h-1.5 rounded-full bg-bg-elevated overflow-hidden">
      <div className={`h-full transition-all ${job.status === "error" ? "bg-rose-500" : "bg-brand-amber"}`}
        style={{ width: `${job.total ? Math.round((job.done / job.total) * 100) : 0}%` }} />
    </div>
    {job.errors?.length > 0 && (
      <div className="mt-2 space-y-0.5 max-h-28 overflow-y-auto" data-testid="rip-job-errors">
        {job.errors.map((e, i) => (
          <div key={i} className="text-[10px] text-amber-300 font-mono truncate">⚠ {e.track}: {e.error}</div>
        ))}
      </div>
    )}
  </div>
);

export const DownloadManager = ({ sessionId, albums, onChanged, openSignal, prefillAlbum }) => {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState([]);
  const [fmt, setFmt] = useState("original");
  const [building, setBuilding] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyScope, setVerifyScope] = useState("session");

  useEffect(() => {
    if (openSignal) setOpen(true);
  }, [openSignal]);

  useEffect(() => {
    if (prefillAlbum?.albumId) {
      setOpen(true);
      setPicked([prefillAlbum.albumId]);
    }
  }, [prefillAlbum]);

  const [zipJob, setZipJob, pollZip] = usePollJob(async (j) => {
    setBuilding(false);
    if (j.status === "error") { toast.error("ZIP build failed — see errors below"); return; }
    toast.success(`ZIP ready — ${j.done - (j.errors?.length || 0)}/${j.total} files packed${j.errors?.length ? `, ${j.errors.length} failed` : ""}`);
    try {
      const r = await api.get(`/rip/jobs/${j.job_id}/download`, { responseType: "blob" });
      downloadBlob(r.data, `broadcastflow_albums_${fmt}.zip`);
    } catch { toast.error("Couldn't download the ZIP"); }
  });

  const [vJob, setVJob, pollVerify] = usePollJob((j) => {
    setVerifying(false);
    onChanged?.();
    if (j.status === "error") { toast.error("Verification failed"); return; }
    const c = j.result?.counts || {};
    const missing = j.result?.missing_tracks || [];
    toast[(c.error || c.warning) ? "warning" : "success"](
      `Verified ${j.total} files — ${c.ok || 0} OK, ${c.warning || 0} warnings, ${c.error || 0} corrupted${
        missing.length ? `; ${missing.length} album tracks missing vs MusicBrainz listing` : ""}`);
  });

  const togglePick = (id) =>
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const buildZip = async (all) => {
    setBuilding(true);
    setZipJob(null);
    try {
      const body = all ? { session_id: sessionId, format: fmt }
        : { album_ids: picked, format: fmt };
      const { data } = await api.post("/rip/jobs/zip", body);
      setZipJob(data);
      pollZip(data.job_id);
      toast.info(`Building ZIP of ${data.total} tracks${fmt !== "original" ? ` (converting to ${fmt.toUpperCase()})` : ""}…`);
    } catch (e) {
      setBuilding(false);
      toast.error(e?.response?.data?.detail || "Couldn't start the export");
    }
  };

  const runVerify = async () => {
    setVerifying(true);
    setVJob(null);
    try {
      const body = verifyScope === "session"
        ? { scope: "session", id: sessionId }
        : { scope: "album", id: verifyScope };
      const { data } = await api.post("/rip/jobs/verify", body);
      setVJob(data);
      pollVerify(data.job_id);
    } catch (e) {
      setVerifying(false);
      toast.error(e?.response?.data?.detail || "Couldn't start verification");
    }
  };

  const vResult = vJob?.status === "done" ? vJob.result : null;

  return (
    <div className="rounded-xl border border-line bg-bg-surface" data-testid="rip-download-manager">
      <button onClick={() => setOpen((o) => !o)} data-testid="rip-dm-toggle"
        className="w-full flex items-center justify-between px-4 py-2.5 text-xs font-mono uppercase tracking-wider text-ink-muted hover:text-ink-primary transition">
        <span className="inline-flex items-center gap-2"><DownloadCloud className="h-3.5 w-3.5 text-brand-cyan" /> Download Manager &amp; Audio Verification</span>
        {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
      </button>
      {open && (
        <div className="px-4 pb-4 grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* ZIP export */}
          <div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1.5">
              Full album downloads (ZIP, organized Artist / Album / NN - Title)
            </div>
            {albums.length === 0 && <div className="text-xs text-ink-muted">No albums in this session yet.</div>}
            <div className="space-y-1 max-h-36 overflow-y-auto">
              {albums.map((a) => (
                <label key={a.album_id} className="flex items-center gap-2 text-xs cursor-pointer" data-testid={`rip-dm-album-${a.album_id}`}>
                  <input type="checkbox" checked={picked.includes(a.album_id)} onChange={() => togglePick(a.album_id)}
                    className="h-3.5 w-3.5 accent-cyan-400" />
                  <span className="truncate">{a.title}</span>
                  <span className="text-ink-muted font-mono text-[10px]">{a.track_count} trk</span>
                </label>
              ))}
            </div>
            <div className="mt-2.5 flex flex-wrap items-center gap-2">
              <select value={fmt} onChange={(e) => setFmt(e.target.value)} data-testid="rip-dm-format"
                className="rounded-md border border-line bg-bg-elevated px-2 py-1.5 text-xs focus:border-brand-amber focus:outline-none">
                {FORMATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <button onClick={() => buildZip(false)} disabled={building || !picked.length} data-testid="rip-dm-build"
                className="inline-flex items-center gap-1.5 rounded-md bg-brand-cyan/90 text-black px-3 py-1.5 text-xs font-bold disabled:opacity-40">
                {building ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileArchive className="h-3.5 w-3.5" />}
                Download {picked.length || ""} album{picked.length === 1 ? "" : "s"}
              </button>
              <button onClick={() => buildZip(true)} disabled={building} data-testid="rip-dm-build-all"
                className="rounded-md border border-line px-3 py-1.5 text-xs text-ink-secondary hover:text-ink-primary disabled:opacity-40">
                Export everything
              </button>
            </div>
            {zipJob && <Progress job={zipJob} />}
            <div className="mt-2 text-[10px] text-ink-muted font-mono">
              Tags, artwork and track numbers are written into the exported copies. WAV = CD-quality 44.1kHz/16-bit.
            </div>
            <div className="mt-1 text-[10px] text-ink-muted font-mono" data-testid="rip-p2g-note">
              Power2Go: CyberLink exposes no public API, so there's no direct integration — the Audio CD export produces burn-ready
              folders (44.1kHz/16-bit stereo WAV + an .m3u in CD track order) that Power2Go opens directly. Audio you ripped WITH
              Power2Go (MP3/WAV/FLAC) can simply be uploaded above.
            </div>
          </div>

          {/* Verification */}
          <div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1.5">
              Audio file verification (duration, corruption, bitrate, completeness)
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <select value={verifyScope} onChange={(e) => setVerifyScope(e.target.value)} data-testid="rip-verify-scope"
                className="rounded-md border border-line bg-bg-elevated px-2 py-1.5 text-xs focus:border-brand-amber focus:outline-none max-w-[220px]">
                <option value="session">Whole session</option>
                {albums.map((a) => <option key={a.album_id} value={a.album_id}>Album: {a.title}</option>)}
              </select>
              <button onClick={runVerify} disabled={verifying} data-testid="rip-verify-run"
                className="inline-flex items-center gap-1.5 rounded-md bg-brand-amber text-black px-3 py-1.5 text-xs font-bold disabled:opacity-40">
                {verifying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
                Verify audio
              </button>
            </div>
            {vJob && <Progress job={vJob} />}
            {vResult?.missing_tracks?.length > 0 && (
              <div className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-2.5" data-testid="rip-missing-tracks">
                <div className="text-[11px] text-amber-200 font-bold flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" /> Missing vs the MusicBrainz track listing ({vResult.missing_tracks.length} of {vResult.release_track_count}):
                </div>
                <div className="mt-1 text-[10px] text-amber-200/80 font-mono space-y-0.5 max-h-24 overflow-y-auto">
                  {vResult.missing_tracks.map((m) => <div key={m.position}>{String(m.position).padStart(2, "0")} — {m.title}</div>)}
                </div>
              </div>
            )}
            {vResult && !vResult.missing_tracks?.length && vJob?.status === "done" && (
              <div className="mt-2 text-[11px] text-emerald-300 flex items-center gap-1" data-testid="rip-verify-ok">
                <CheckCircle2 className="h-3 w-3" /> {vResult.counts?.ok || 0} verified OK · {vResult.counts?.warning || 0} warnings · {vResult.counts?.error || 0} corrupted
              </div>
            )}
            <div className="mt-2 text-[10px] text-ink-muted font-mono">
              A track is only marked complete after its actual audio decodes cleanly. Results show on each track.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
